import { AuthoritativeMirror, AuthoritativeRevalidationRequiredError, } from "./sync/index.js";
export class OfflineWalletCommandError extends Error {
    constructor() {
        super("Wallet commands require an online home node");
        this.name = "OfflineWalletCommandError";
    }
}
export class WalletFreshStateRequiredError extends Error {
    constructor() {
        super("Wallet state could not be freshly revalidated; confirm again when connected");
        this.name = "WalletFreshStateRequiredError";
    }
}
export class InsufficientWalletBalanceError extends Error {
    amountMsat;
    spendableMsat;
    constructor(amountMsat, spendableMsat) {
        super("The authoritative wallet balance is insufficient for this payment");
        this.name = "InsufficientWalletBalanceError";
        this.amountMsat = amountMsat;
        this.spendableMsat = spendableMsat;
    }
}
export class ExpiredInvoiceError extends Error {
    constructor() {
        super("The invoice has expired");
        this.name = "ExpiredInvoiceError";
    }
}
export class WalletCommandRejectedError extends Error {
    constructor() {
        super("The authoritative wallet command was rejected");
        this.name = "WalletCommandRejectedError";
    }
}
export class WalletMirrorService {
    #id;
    #mirror;
    #now;
    #outbox;
    #strategy;
    #homeNode;
    constructor(dependencies, options) {
        if (!Number.isInteger(options.staleAfterMs) || options.staleAfterMs <= 0) {
            throw new RangeError("Wallet mirror staleAfterMs must be a positive integer");
        }
        this.#homeNode = dependencies.homeNode;
        this.#mirror = dependencies.mirror;
        this.#now = options.now ?? (() => new Date().toISOString());
        this.#id = options.id ?? (() => `wallet:${this.#now()}`);
        this.#outbox = dependencies.outbox;
        this.#strategy = new AuthoritativeMirror({
            apply: async (snapshot) => await this.#mirror.writeSnapshot(snapshot),
            execute: async (command) => await this.#execute(command),
            staleAfterMs: options.staleAfterMs,
        });
    }
    async applyDelta(delta) {
        return await this.#strategy.applyDelta(delta, { domain: "wallet", now: this.#now() });
    }
    async readBalance() {
        const snapshot = await this.#mirror.readSnapshot();
        if (snapshot === undefined)
            return { freshness: "unavailable" };
        return {
            balanceMsat: snapshot.value.balanceMsat,
            freshness: this.#isFresh(snapshot) ? "fresh" : "stale",
            observedAt: snapshot.observedAt,
            spendableMsat: snapshot.value.spendableMsat,
            staleAt: snapshot.staleAt,
        };
    }
    async listPayments() {
        const snapshot = await this.#mirror.readSnapshot();
        if (snapshot === undefined)
            return { freshness: "unavailable", items: [] };
        return {
            freshness: this.#isFresh(snapshot) ? "fresh" : "stale",
            items: snapshot.value.payments,
            observedAt: snapshot.observedAt,
            staleAt: snapshot.staleAt,
        };
    }
    async listL402Receipts() {
        const snapshot = await this.#mirror.readSnapshot();
        if (snapshot === undefined)
            return { freshness: "unavailable", items: [] };
        return {
            freshness: this.#isFresh(snapshot) ? "fresh" : "stale",
            items: snapshot.value.l402,
            observedAt: snapshot.observedAt,
            staleAt: snapshot.staleAt,
        };
    }
    async createInvoice(input) {
        requirePositiveMsat(input.amountMsat, "invoice amount");
        requireText(input.idempotencyKey, "invoice idempotency key");
        return await this.#runCommand({
            amountMsat: input.amountMsat,
            ...(input.description === undefined ? {} : { description: input.description }),
            kind: "create-invoice",
        }, input.idempotencyKey, (snapshot) => {
            if (snapshot.value.balanceMsat < 0)
                throw new WalletFreshStateRequiredError();
        });
    }
    async payInvoice(input) {
        requirePositiveMsat(input.amountMsat, "payment amount");
        requireText(input.idempotencyKey, "payment idempotency key");
        requireText(input.invoiceId, "invoice id");
        if (input.expiresAt !== undefined && isExpired(input.expiresAt, this.#now()))
            throw new ExpiredInvoiceError();
        return await this.#runCommand({
            amountMsat: input.amountMsat,
            ...(input.expiresAt === undefined ? {} : { expiresAt: input.expiresAt }),
            invoiceId: input.invoiceId,
            kind: "pay-invoice",
        }, input.idempotencyKey, (snapshot) => {
            if (input.amountMsat > snapshot.value.spendableMsat) {
                throw new InsufficientWalletBalanceError(input.amountMsat, snapshot.value.spendableMsat);
            }
        });
    }
    async #runCommand(command, idempotencyKey, validate) {
        const cached = await this.#mirror.readCommandResult(idempotencyKey);
        if (cached !== undefined)
            return cached;
        this.#assertOnline();
        const snapshot = await this.#ensureFresh();
        validate(snapshot);
        const outbox = this.#outboxRecord(command, idempotencyKey);
        await this.#outbox.create(outbox);
        try {
            const result = await this.#strategy.runCommand({ idempotencyKey, payload: command }, {
                now: this.#now(),
                revalidate: async () => {
                    if (!this.#isOnline())
                        return false;
                    try {
                        const refreshed = await this.#ensureFresh();
                        validate(refreshed);
                        return true;
                    }
                    catch (error) {
                        if (error instanceof InsufficientWalletBalanceError)
                            throw error;
                        return false;
                    }
                },
            });
            await this.#mirror.writeCommandResult(idempotencyKey, result);
            await this.#outbox.updateState(outbox.id, "acknowledged");
            return result;
        }
        catch (error) {
            await this.#outbox.updateState(outbox.id, "failed");
            if (error instanceof AuthoritativeRevalidationRequiredError)
                throw new WalletFreshStateRequiredError();
            if (error instanceof OfflineWalletCommandError || error instanceof InsufficientWalletBalanceError || error instanceof ExpiredInvoiceError)
                throw error;
            throw error instanceof WalletFreshStateRequiredError ? error : new WalletCommandRejectedError();
        }
    }
    async #ensureFresh() {
        const current = await this.#mirror.readSnapshot();
        if (current !== undefined && this.#isFresh(current))
            return current;
        this.#assertOnline();
        try {
            const response = await this.#homeNode.invoke({ operation: "wallet.authoritative-snapshot", payload: {} });
            const payload = parseSnapshotPayload(response);
            await this.applyDelta({ cursor: `refresh:${this.#id()}`, id: this.#id(), payload });
        }
        catch (error) {
            if (error instanceof OfflineWalletCommandError)
                throw error;
            throw new WalletFreshStateRequiredError();
        }
        const refreshed = await this.#mirror.readSnapshot();
        if (refreshed === undefined || !this.#isFresh(refreshed))
            throw new WalletFreshStateRequiredError();
        return refreshed;
    }
    async #execute(command) {
        this.#assertOnline();
        try {
            const response = await this.#homeNode.invoke({
                operation: command.payload.kind === "create-invoice" ? "wallet.create-invoice" : "wallet.pay-invoice",
                payload: { ...command.payload, idempotencyKey: command.idempotencyKey },
            });
            return command.payload.kind === "create-invoice"
                ? parseInvoiceResult(response, command.payload.amountMsat)
                : parsePaymentResult(response, command.payload.amountMsat);
        }
        catch (error) {
            if (error instanceof OfflineWalletCommandError)
                throw error;
            throw new WalletCommandRejectedError();
        }
    }
    #outboxRecord(command, idempotencyKey) {
        const createdAt = this.#now();
        return {
            createdAt,
            domain: "wallet",
            id: `wallet:${idempotencyKey}`,
            idempotencyKey,
            payload: { ...command, operation: command.kind === "create-invoice" ? "wallet.create-invoice" : "wallet.pay-invoice" },
            state: "pending",
            updatedAt: createdAt,
        };
    }
    #assertOnline() {
        if (!this.#isOnline())
            throw new OfflineWalletCommandError();
    }
    #isOnline() { return this.#homeNode.state.status === "connected"; }
    #isFresh(snapshot) { return Date.parse(this.#now()) < Date.parse(snapshot.staleAt); }
}
function parseSnapshotPayload(value) {
    const record = asRecord(value, "wallet snapshot");
    return {
        balanceMsat: positiveOrZero(record.balanceMsat, "wallet balance"),
        l402: parseL402(record.l402),
        payments: parsePayments(record.payments),
        spendableMsat: positiveOrZero(record.spendableMsat, "wallet spendable balance"),
    };
}
function parseInvoiceResult(value, amountMsat) {
    const record = asRecord(value, "invoice result");
    const invoiceId = text(record.invoiceId, "invoice id");
    const paymentRequest = text(record.paymentRequest, "payment request");
    return {
        amountMsat: numberOrDefault(record.amountMsat, amountMsat),
        ...(record.paymentHash === undefined ? {} : { paymentHash: text(record.paymentHash, "payment hash") }),
        invoiceId,
        kind: "invoice-created",
        paymentRequest,
        status: "created",
    };
}
function parsePaymentResult(value, amountMsat) {
    const record = asRecord(value, "payment result");
    const status = record.status ?? "pending";
    if (status !== "pending" && status !== "succeeded" && status !== "failed")
        throw new WalletCommandRejectedError();
    return {
        amountMsat: numberOrDefault(record.amountMsat, amountMsat),
        ...(record.paymentHash === undefined ? {} : { paymentHash: text(record.paymentHash, "payment hash") }),
        kind: "payment-submitted",
        paymentId: text(record.paymentId, "payment id"),
        status,
    };
}
function parsePayments(value) {
    if (value === undefined)
        return [];
    if (!Array.isArray(value))
        throw new WalletFreshStateRequiredError();
    return value.map((item) => {
        const record = asRecord(item, "payment record");
        const direction = record.direction;
        const status = record.status;
        if (direction !== "incoming" && direction !== "outgoing")
            throw new WalletFreshStateRequiredError();
        if (status !== "pending" && status !== "succeeded" && status !== "failed" && status !== "expired")
            throw new WalletFreshStateRequiredError();
        return {
            amountMsat: positiveOrZero(record.amountMsat, "payment amount"),
            createdAt: timestamp(record.createdAt, "payment createdAt"),
            direction,
            ...(record.failureCode === undefined ? {} : { failureCode: text(record.failureCode, "payment failure code") }),
            id: text(record.id, "payment id"),
            ...(record.paymentHash === undefined ? {} : { paymentHash: text(record.paymentHash, "payment hash") }),
            status,
            updatedAt: timestamp(record.updatedAt, "payment updatedAt"),
        };
    });
}
function parseL402(value) {
    if (value === undefined)
        return [];
    if (!Array.isArray(value))
        throw new WalletFreshStateRequiredError();
    return value.map((item) => {
        const record = asRecord(item, "L402 receipt");
        return {
            amountMsat: positiveOrZero(record.amountMsat, "L402 amount"),
            paidAt: timestamp(record.paidAt, "L402 paidAt"),
            ...(record.paymentHash === undefined ? {} : { paymentHash: text(record.paymentHash, "L402 payment hash") }),
            resource: text(record.resource, "L402 resource"),
            receiptId: text(record.receiptId, "L402 receipt id"),
        };
    });
}
function asRecord(value, _label) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        throw new WalletFreshStateRequiredError();
    return value;
}
function text(value, _label) {
    if (typeof value !== "string" || value.trim() === "")
        throw new WalletCommandRejectedError();
    return value;
}
function timestamp(value, label) {
    const parsed = text(value, label);
    if (!Number.isFinite(Date.parse(parsed)))
        throw new WalletFreshStateRequiredError();
    return parsed;
}
function positiveOrZero(value, _label) {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0)
        throw new WalletFreshStateRequiredError();
    return value;
}
function numberOrDefault(value, fallback) {
    return value === undefined ? fallback : positiveOrZero(value, "amount");
}
function requirePositiveMsat(value, label) {
    if (!Number.isSafeInteger(value) || value <= 0)
        throw new TypeError(`${label} must be a positive integer`);
}
function requireText(value, label) {
    if (value.trim() === "")
        throw new TypeError(`${label} must be a non-empty string`);
}
function isExpired(expiresAt, now) {
    const expiry = Date.parse(expiresAt);
    if (!Number.isFinite(expiry))
        throw new TypeError("invoice expiry must be an ISO timestamp");
    return expiry <= Date.parse(now);
}
//# sourceMappingURL=wallet-mirror-service.js.map